import api from './api';
import { buildQueryParams } from './utils/query-params';
import type {
  ApiResponse,
  PaginatedResponse,
  LabOrder,
  LabOrderFilters,
  LabOrderScope,
  CreateLabOrderData,
  UpdateLabOrderData,
  CancelLabOrderData,
  SetLabOrderEtaData,
  LabOrderTravelWindow,
  LabOrderPhase,
  LabOrderMaterial,
} from '@/types';

export const getLabOrders = async (filters: LabOrderFilters = {}) => {
  const query = buildQueryParams(filters);
  const response = await api.get<PaginatedResponse<LabOrder>>(
    `/lab-orders${query ? `?${query}` : ''}`,
  );
  return response.data;
};

/**
 * Per-role dashboard — the "in flight" lab-order slice: everything that has
 * left draft but is not yet delivered/cancelled, soonest due-date first.
 * Thin wrapper over {@link getLabOrders}; the status array serializes as
 * `status[]=…` via buildQueryParams, matching LabOrderController's array filter.
 *
 * @param scope `mine` (dentist's own orders) | `unassigned` | `all` — defaults
 *              to the server's own scope when omitted.
 */
export const getInFlightLabOrders = async (
  limit = 5,
  scope?: LabOrderScope,
) => {
  return getLabOrders({
    scope,
    status: ['sent', 'metal_tryin_ready', 'dentin_tryin_ready', 'ready'],
    sort: 'due_date',
    direction: 'asc',
    page: 1,
    per_page: limit,
  });
};

export const getLabOrder = async (id: string) => {
  const response = await api.get<ApiResponse<LabOrder>>(`/lab-orders/${id}`);
  return response.data.data;
};

export const createLabOrder = async (data: CreateLabOrderData) => {
  const response = await api.post<ApiResponse<LabOrder>>('/lab-orders', data);
  return response.data.data;
};

export const updateLabOrder = async (id: string, data: UpdateLabOrderData) => {
  const response = await api.put<ApiResponse<LabOrder>>(`/lab-orders/${id}`, data);
  return response.data.data;
};

export const claimLabOrder = async (id: string) => {
  const response = await api.post<ApiResponse<LabOrder>>(`/lab-orders/${id}/claim`);
  return response.data.data;
};

/**
 * Advance the order — which on a phased order CLOSES the live phase and hands
 * over the next one. Takes nothing: the finish moment is recorded server-side,
 * and what the technician stated is the phase's own due date/time.
 */
export const advanceLabOrderStatus = async (id: string) => {
  const response = await api.post<ApiResponse<LabOrder>>(
    `/lab-orders/${id}/advance-status`,
  );
  return response.data.data;
};

export const setLabOrderEta = async (id: string, data: SetLabOrderEtaData) => {
  const response = await api.post<ApiResponse<LabOrder>>(`/lab-orders/${id}/eta`, data);
  return response.data.data;
};

export const cancelLabOrder = async (id: string, data: CancelLabOrderData) => {
  const response = await api.post<ApiResponse<LabOrder>>(`/lab-orders/${id}/cancel`, data);
  return response.data.data;
};

/**
 * Lead-detail tab variant — list lab orders for a specific lead.
 * Re-uses the same /lab-orders endpoint with a lead_id filter. Post Phase 59
 * (LCC-06) lab orders are owned by the lead's Client (lab_orders.client_id);
 * the server resolves client_id from the supplied lead_id, so the request
 * shape stays lead-keyed (OQ-2) and no frontend change is needed.
 */
export const getLeadLabOrders = async (leadId: string) => {
  const response = await api.get<PaginatedResponse<LabOrder>>(
    `/lab-orders?lead_id=${encodeURIComponent(leadId)}&per_page=100`,
  );
  return response.data;
};

/**
 * Compose-time flight window for a patient who has no lab order yet — the
 * Dentour slip form prefills its due date from `departure_date` (260818 client
 * ask). Same two-key shape as {@link LabOrder.travel_window}; slip-mode only
 * (404 elsewhere), gated server-side on lab_orders.create|update so a doctor
 * without lead_travel.view can still fill their own slip.
 *
 * Resolves to null for a patient with no flight info — the common case the
 * clinic keeps entering by hand.
 */
export const getLeadTravelWindow = async (
  leadId: string,
): Promise<LabOrderTravelWindow | null> => {
  const response = await api.get<ApiResponse<LabOrderTravelWindow | null>>(
    `/lab-orders/travel-window/${encodeURIComponent(leadId)}`,
  );
  return response.data.data;
};


/**
 * Date the ACTIVE fabrication phase (or amend its note). Only the active phase
 * is writable — a phase is dated once the one before it is done.
 */
export const updateLabOrderPhase = async (
  orderId: string,
  phaseId: string,
  data: { due_date?: string | null; due_time?: string | null; note?: string | null },
) => {
  const response = await api.put<ApiResponse<LabOrder>>(
    `/lab-orders/${orderId}/phases/${phaseId}`,
    data,
  );
  return response.data.data;
};

/**
 * Move one fabrication phase to a different bench. Works on any phase that has
 * not settled — unlike dating, which only the ACTIVE phase accepts — because
 * the point is to re-staff a step before it comes round. Null clears the owner.
 */
export const assignLabOrderPhase = async (
  orderId: string,
  phaseId: string,
  assignedTo: number | null,
) => {
  const response = await api.put<ApiResponse<LabOrder>>(
    `/lab-orders/${orderId}/phases/${phaseId}/assignee`,
    { assigned_to: assignedTo },
  );
  return response.data.data;
};

/** Active accounts a fabrication bench can be handed to. */
export const getLabPhaseAssignees = async (): Promise<
  { id: number; full_name: string }[]
> => {
  const response = await api.get<ApiResponse<{ id: number; full_name: string }[]>>(
    '/lab-orders/phase-assignees',
  );
  return response.data.data;
};

/** Admin-only correction of a mistakenly closed phase. */
export const reopenLabOrderPhase = async (orderId: string, phaseId: string) => {
  const response = await api.post<ApiResponse<LabOrder>>(
    `/lab-orders/${orderId}/phases/${phaseId}/reopen`,
    {},
  );
  return response.data.data;
};

/**
 * ArtDent 261007 — the lab takes each step as it comes: take the step in
 * progress. 409 when someone else took it first.
 */
export const takeLabOrderPhase = async (orderId: string, phaseId: string) => {
  const response = await api.post<ApiResponse<LabOrder>>(
    `/lab-orders/${orderId}/phases/${phaseId}/take`,
    {},
  );
  return response.data.data;
};

/** ArtDent 261007 — the resin try-in went well: milling starts. Order's doctor or admin. */
export const approveLabTryin = async (orderId: string, phaseId: string) => {
  const response = await api.post<ApiResponse<LabOrder>>(
    `/lab-orders/${orderId}/phases/${phaseId}/tryin-approve`,
    {},
  );
  return response.data.data;
};

/**
 * ArtDent 261007 — the resin try-in needs changes: the notes go back to the lab
 * with another Design and Resin try-in before Milling.
 */
export const requestLabTryinChanges = async (orderId: string, phaseId: string, notes: string) => {
  const response = await api.post<ApiResponse<LabOrder>>(
    `/lab-orders/${orderId}/phases/${phaseId}/tryin-changes`,
    { notes },
  );
  return response.data.data;
};

/**
 * The materials this clinic's bench works — the per-clinic subset of the
 * material enum. The form renders only these; the full enum stays valid for
 * reading orders already written on a material since dropped.
 */
export const getLabOrderMaterials = async (): Promise<LabOrderMaterial[]> => {
  const response = await api.get<ApiResponse<LabOrderMaterial[]>>(
    '/lab-orders/materials',
  );
  return response.data.data;
};
